import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MerchantService, PaymentCard, PaymentCardService } from '@galaxy/billing';
import { Country, CountryStateService, State } from '@vendasta/country-state';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, Subscription, from } from 'rxjs';
import { distinctUntilChanged, finalize, switchMap, take, tap } from 'rxjs/operators';
import { Script, StripeService } from '../stripe.service';
import { PaymentMethodInfoComponent } from './payment-method-info.component';

declare let Stripe; // : stripe.StripeStatic;

interface PaymentData {
  merchantId: string;
  stripePublicKey: string;
  requireRecaptcha: boolean;
  recaptchaKey?: string;
  paymentCard?: PaymentCard;
}

interface TokenData {
  cardholderName: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

@Component({
  selector: 'billing-ui-edit-payment-method-dialog',
  templateUrl: './edit-payment-method-dialog.component.html',
  styleUrls: ['./edit-payment-method-dialog.component.scss'],
})
export class EditPaymentMethodDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  public merchantId: string;
  public stripePublicKey: string;
  public recaptchaKey: string;
  public requireRecaptcha: boolean;
  public paymentCard: PaymentCard | null;

  public formGroup: UntypedFormGroup;
  public countries$: Observable<Country[]>;
  public states$$: BehaviorSubject<State[]> = new BehaviorSubject<State[]>([]);
  public stateRequired = true;

  public stripeLoader: Observable<Script>;
  private stripe; // : stripe.Stripe;

  private cardNumberElement;
  private cardNumberPlaceholder: string;
  private cardNumberValid = false;
  private cardExpiryElement;
  private cardExpiryPlaceholder: string;
  private cardExpiryValid = false;
  private cardCvcElement;
  private cardCvcPlaceholder: string;
  private cardCvcValid = false;

  private subscriptions: Subscription[] = [];

  public isSaving$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    public dialogRef: MatDialogRef<PaymentMethodInfoComponent, boolean>,
    private formBuilder: UntypedFormBuilder,
    private countryStateService: CountryStateService,
    private stripeService: StripeService,
    private alertService: SnackbarService,
    private paymentCardService: PaymentCardService,
    private merchantService: MerchantService,
    @Inject(MAT_DIALOG_DATA) public data: PaymentData,
  ) {
    this.merchantId = data.merchantId;
    this.stripePublicKey = data.stripePublicKey;
    this.requireRecaptcha = data.requireRecaptcha;
    this.recaptchaKey = data.recaptchaKey || '';
    this.paymentCard = data.paymentCard || null;
    this.stripeLoader = this.stripeService.loadScript();

    this.cardCvcPlaceholder = '***';
    this.cardNumberPlaceholder = '**** **** **** ****';
    if (this.paymentCard && this.paymentCard.expiryMonth && this.paymentCard.expiryYear) {
      this.cardExpiryPlaceholder = this.paymentCard.expiryMonth + ' / ' + String(this.paymentCard.expiryYear).slice(-2);
    } else {
      this.cardExpiryPlaceholder = 'mm/yy';
    }

    this.countries$ = this.countryStateService.getCountriesOptions();

    const recaptchaValidator = this.requireRecaptcha ? Validators.required : null;
    this.formGroup = this.formBuilder.group({
      recaptcha: [null, recaptchaValidator],
      useContactAddress: [false],
      cardholderName: ['', Validators.required],
      address: ['', Validators.required],
      city: ['', Validators.required],
      state: [{ value: '', disabled: !this.stateRequired }, Validators.required],
      country: ['', Validators.required],
      zipCode: ['', Validators.required],
    });
  }

  get country(): UntypedFormControl {
    return this.formGroup.get('country') as UntypedFormControl;
  }

  get state(): UntypedFormControl {
    return this.formGroup.get('state') as UntypedFormControl;
  }

  get address(): UntypedFormControl {
    return this.formGroup.get('address') as UntypedFormControl;
  }

  get city(): UntypedFormControl {
    return this.formGroup.get('city') as UntypedFormControl;
  }

  get zipCode(): UntypedFormControl {
    return this.formGroup.get('zipCode') as UntypedFormControl;
  }

  get useContactAddress(): UntypedFormControl {
    return this.formGroup.get('useContactAddress') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.country.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap(() => this.state.patchValue('')), // reset state selection if country changes
          switchMap((countryCode) => this.countryStateService.getStatesOptions(countryCode)),
        )
        .subscribe((states) => {
          this.states$$.next(states);
          if (states.length > 0) {
            this.stateRequired = true;
            this.state.setValidators(Validators.required);
          } else {
            this.stateRequired = false;
            this.state.clearValidators();
          }
          if (this.paymentCard && states.filter((s) => s.code === this.paymentCard.state)) {
            this.state.patchValue(this.paymentCard.state);
          }
          this.formGroup.updateValueAndValidity();
        }),
    );

    this.subscriptions.push(
      this.useContactAddress.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((useContactAddress) => this.setAddressFields(useContactAddress)),
    );

    if (this.paymentCard) {
      this.formGroup.patchValue(this.paymentCard);
    }
  }

  ngAfterViewInit(): void {
    this.stripeLoader.pipe(take(1)).subscribe(() => this.mountStripe(this.stripePublicKey));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private mountStripe(stripeKey: string): void {
    this.stripe = Stripe(stripeKey);
    const elements = this.stripe.elements();

    const inputs = document.querySelectorAll('.fancy-card .input');
    Array.prototype.forEach.call(inputs, (input) => {
      input.addEventListener('focus', () => {
        input.classList.add('focused');
      });
      input.addEventListener('blur', () => {
        input.classList.remove('focused');
      });
      input.addEventListener('keyup', () => {
        if (input.value && input.value.length === 0) {
          input.classList.add('empty');
        } else {
          input.classList.remove('empty');
        }
      });
    });

    const style = {
      base: {
        iconColor: '#666EE8',
        color: 'black',
        fontFamily: 'Roboto, "Helvetica Neue", Helvetica, sans-serif',
        fontSize: '16px',

        '::placeholder': {
          color: '#949494',
          fontSize: '16px',
        },
      },
      invalid: {
        iconColor: '#e85746',
        color: '#e85746',
      },
    };

    const elementClasses = {
      focus: 'focused',
      empty: 'empty',
      invalid: 'invalid',
    };

    this.cardNumberElement = elements.create('cardNumber', {
      style: style,
      placeholder: this.cardNumberPlaceholder,
      hideIcon: false,
      classes: elementClasses,
    });
    this.cardNumberElement.mount('#card-number-element');
    this.cardNumberElement.addEventListener('change', (event) => {
      this.checkStripeError('.card-number-error', event, (valid) => {
        this.cardNumberValid = valid && !event.empty;
      });
    });

    this.cardExpiryElement = elements.create('cardExpiry', {
      style: style,
      placeholder: this.cardExpiryPlaceholder,
      classes: elementClasses,
    });
    this.cardExpiryElement.mount('#card-expiry-element');
    this.cardExpiryElement.addEventListener('change', (event) => {
      this.checkStripeError('.card-expiry-error', event, (valid) => {
        this.cardExpiryValid = valid && !event.empty;
      });
    });

    this.cardCvcElement = elements.create('cardCvc', {
      style: style,
      placeholder: this.cardCvcPlaceholder,
      classes: elementClasses,
    });
    this.cardCvcElement.mount('#card-cvc-element');
    this.cardCvcElement.addEventListener('change', (event) => {
      this.checkStripeError('.card-cvc-error', event, (valid) => {
        this.cardCvcValid = valid && !event.empty;
      });
    });
  }

  public onSave(): void {
    this.isSaving$$.next(true);
    const { useContactAddress, cardholderName, address, city, state, country, zipCode } = this.formGroup.getRawValue();
    if (useContactAddress) {
      this.merchantService
        .get(this.merchantId)
        .pipe(take(1))
        .subscribe((merchant) => {
          const tokenData: TokenData = {
            cardholderName: merchant.contactName,
            address: merchant.address,
            city: merchant.city,
            state: merchant.state,
            country: merchant.country,
            zipCode: merchant.zipCode,
          };
          this.submitCreditCard(tokenData);
        });
    } else {
      const tokenData: TokenData = {
        cardholderName,
        address,
        city,
        state,
        country,
        zipCode,
      };
      this.submitCreditCard(tokenData);
    }
  }

  public onClose(wasUpdated: boolean): void {
    this.unmountStripe();
    this.dialogRef.close(wasUpdated);
  }

  private isValid(): boolean {
    return this.formGroup.valid && this.cardNumberValid && this.cardExpiryValid && this.cardCvcValid;
  }

  public isDisabled(): boolean {
    return !this.isValid() || this.isSaving$$.getValue();
  }

  public showAddressFields(): boolean {
    return !this.useContactAddress.value;
  }

  private unmountStripe(): void {
    this.cardNumberElement.unmount();
    this.cardExpiryElement.unmount();
    this.cardCvcElement.unmount();
  }

  private submitCreditCard(tokenData: TokenData): void {
    const stripeResult: Observable<{ token: { id: string }; error: Error }> = from(
      this.stripe.createToken(this.cardNumberElement, {
        name: tokenData.cardholderName,
        address_line1: tokenData.address,
        address_city: tokenData.city,
        address_state: tokenData.state,
        address_country: tokenData.country,
        address_zip: tokenData.zipCode,
      }) as Observable<{ token: { id: string }; error: Error }>,
    );

    stripeResult
      .pipe(
        take(1),
        switchMap((result) => {
          if (Object.prototype.hasOwnProperty.call(result, 'error')) {
            throw new Error(result.error.message);
          }
          if (this.paymentCard) {
            return this.paymentCardService.update(this.merchantId, result.token.id);
          } else {
            return this.paymentCardService.create(this.merchantId, result.token.id);
          }
        }),
        finalize(() => this.isSaving$$.next(false)),
      )
      .subscribe(
        () => {
          this.alertService.openSuccessSnack('Successfully updated payment method.');
          this.onClose(true);
        },
        (err) => {
          if (err instanceof Error) {
            this.alertService.openErrorSnack(err.message);
          } else {
            this.alertService.openErrorSnack(err.error.message);
          }
        },
      );
  }

  private setAddressFields(useContactAddress: boolean): void {
    if (useContactAddress) {
      this.formGroup.removeControl('address');
      this.formGroup.removeControl('city');
      this.formGroup.removeControl('state');
      this.formGroup.removeControl('country');
      this.formGroup.removeControl('zipCode');
    } else {
      this.formGroup.addControl(
        'address',
        new UntypedFormControl(this.paymentCard ? this.paymentCard.address : '', [Validators.required]),
      );
      this.formGroup.addControl(
        'city',
        new UntypedFormControl(this.paymentCard ? this.paymentCard.city : '', [Validators.required]),
      );
      this.formGroup.addControl(
        'state',
        new UntypedFormControl(
          { value: this.paymentCard ? this.paymentCard.state : '', disabled: !this.stateRequired },
          [Validators.required],
        ),
      );
      this.formGroup.addControl(
        'country',
        new UntypedFormControl(this.paymentCard ? this.paymentCard.country : '', [Validators.required]),
      );
      this.formGroup.addControl(
        'zipCode',
        new UntypedFormControl(this.paymentCard ? this.paymentCard.zipCode : '', [Validators.required]),
      );
    }
    this.formGroup.updateValueAndValidity();
  }

  private checkStripeError(errorDomSelector: string, event: any, callback: (input: boolean) => any): void {
    const errorElement = document.querySelector(errorDomSelector);
    if (event.error) {
      callback(false);
      errorElement.textContent = event.error.message;
    } else {
      callback(true);
      errorElement.textContent = '';
    }
  }
}
